import Table from "components/Table";
import { ConsensusMeterCategory } from "helpers/api";
import pluralize from "helpers/pluralize";
import useLabels from "hooks/useLabels";
import { useConsensusSnapshot } from "../hooks/useConsensusSnapshot";
import { CategoryAggregations, MeterCategories } from "../types";
import { orderedCategoryAggregations } from "../utils";
import { AggregationResult } from "./AggregationResult";
import { CategorySummary } from "./CategorySummary";

interface CategoryResultsRowProps {
  category: MeterCategories;
  categoryData: ConsensusMeterCategory | null;
  numResultsAnalyzed: number;
  categoryWins: CategoryAggregations[];
  aggregationTies: CategoryAggregations[];
  consensusMeterSuccess: boolean;
  isFilteringEnabled: boolean;
  threadInteractionId?: string;
  searchId?: string;
}

export const CategoryResultsRow = ({
  category,
  categoryData,
  numResultsAnalyzed,
  categoryWins,
  aggregationTies,
  consensusMeterSuccess,
  isFilteringEnabled,
  threadInteractionId,
  searchId,
}: CategoryResultsRowProps) => {
  return (
    <Table.TR
      data-testid={`${category}-row`}
      className="border-b border-border-base"
    >
      <Table.TD>
        <div className="flex flex-col gap-3">
          <CategorySummary
            category={category}
            categoryData={categoryData}
            numResultsAnalyzed={numResultsAnalyzed}
            categoryWins={categoryWins}
            consensusMeterSuccess={consensusMeterSuccess}
            display="flex"
            isFilteringEnabled={isFilteringEnabled}
            threadInteractionId={threadInteractionId}
          />
          {categoryData && (
            <CategoryAnalysis
              category={category}
              categoryData={categoryData}
              searchId={searchId}
            />
          )}
        </div>
      </Table.TD>
      {orderedCategoryAggregations.map((aggregation) => (
        <Table.TD
          className="py-4 pr-9 pl-0 align-top"
          key={aggregation + "-" + category}
        >
          {consensusMeterSuccess && !categoryData && (
            <p className="sm-normal text-fg-muted">—</p>
          )}
          {categoryData && (
            <AggregationResult
              aggregation={aggregation}
              value={categoryData[aggregation]}
              winner={categoryWins.includes(aggregation)}
              tied={aggregationTies.includes(aggregation)}
            />
          )}
        </Table.TD>
      ))}
    </Table.TR>
  );
};

interface CategoryAnalysisProps {
  category: MeterCategories;
  categoryData: ConsensusMeterCategory;
  searchId?: string;
}

const CategoryAnalysis = ({
  category,
  categoryData,
  searchId,
}: CategoryAnalysisProps) => {
  const { summary, isLoading, isNotSignedIn, isOutOfCredits } =
    useConsensusSnapshot(searchId);
  const [
    paperCount,
    analysisPlaceholder,
    analysisFallback,
    analysisSummarizing,
  ] = useLabels(
    "consensus-meter.paper-count",
    "consensus-meter.analysis-placeholder",
    "consensus-meter.analysis-fallback",
    "consensus-meter.analysis-summarizing"
  );

  const paperCountMarkup = (
    <span className="text-fg-muted">
      {isLoading && `${analysisSummarizing} `}
      {pluralize(paperCount, {
        PAPER_COUNT: categoryData.paper_count,
      })}{" "}
      —{" "}
    </span>
  );

  if (isNotSignedIn || isOutOfCredits) {
    return (
      <p>
        {paperCountMarkup}
        <span className="text-fg-muted blur-xs select-none">
          {analysisPlaceholder}
        </span>
      </p>
    );
  }

  return (
    <div className="sm-normal">
      {isLoading ? (
        <div data-testid={`${category}-summary-loading`}>
          <div className="flex items-center whitespace-nowrap">
            {paperCountMarkup}
            <div className="flex-1 h-3 ml-1 bg-border-base rounded-full animate-pulse relative top-px" />
          </div>
          <div className="h-3 bg-border-base rounded-full animate-pulse mt-1 " />
        </div>
      ) : (
        <p>
          {paperCountMarkup}
          <span className="text-fg-base">
            {summary?.[category] || analysisFallback}
          </span>
        </p>
      )}
    </div>
  );
};
